import React, { FC, useEffect, useMemo, useState } from 'react'
import HorizontalSelector from 'views/components/Selectors/HorizontalSelector'
import BrainMap from 'views/components/Graphics/BrainMap'
import TeamMapGrid from '../Grids/TeamMapGrid'
import {
  Container,
  SelectorContainer,
  InsightsStack,
  SummaryGrid,
  SummaryCard,
  SummaryLabel,
  SummaryValue,
  SummaryHint,
  EnvironmentBanner,
  EnvironmentTitle,
  EnvironmentText,
  InsightSection,
  InsightSectionTitle,
  InsightPills,
  InsightPill,
  ContentCard,
  ContextNote,
  ContextNoteText,
} from './styled'
import getBetesaResults from 'services/competence/getBetesaResults'
import { useNotification } from 'lib/context/notification.context'
import iconInfo from '../../../views/components/assets/iconInfo.png'
import {
  NoDataContainer,
  NoDataIcon,
  NoDataSubContainer,
  NoDataText,
  TextContainer,
} from 'views/screens/Platform-administrator/Analytics/styled'
import Loader from 'views/components/UI/Loader'
import { BetesaResultsResponse } from './store/entities'
import getQuadrantResults from 'services/competence/getQuadrantResults'
import getTalentsResults from 'services/competence/getTalentsResults'
import getPlanResults from 'services/competence/getPlanResults'
import getBrainResults from 'services/competence/getBrainResults'
import BrainGraphicContainer from './BrainGraphicContainer'
import getBrainIdealProfile from 'services/competence/getBrainIdealProfile'
import saveBrainIdealProfile, { BrainIdealProfile } from 'services/competence/saveBrainIdealProfile'

const QUADRANT_DETAILS = {
  FI: {
    shortName: 'FI',
    displayName: 'Frontal izquierdo',
    summary: 'analisis estructurado y pensamiento critico',
    environment: 'Entornos con objetivos claros, espacios de analisis y decisiones soportadas en datos.',
  },
  FD: {
    shortName: 'FD',
    displayName: 'Frontal derecho',
    summary: 'vision, estrategia y cambio',
    environment: 'Entornos con retos nuevos, espacio para idear y margen para explorar alternativas.',
  },
  BI: {
    shortName: 'BI',
    displayName: 'Basal izquierdo',
    summary: 'orden, proceso y continuidad',
    environment: 'Entornos con estabilidad operativa, seguimiento disciplinado y claridad de metodo.',
  },
  BD: {
    shortName: 'BD',
    displayName: 'Basal derecho',
    summary: 'relacionamiento, servicio y cohesion',
    environment: 'Entornos colaborativos, cercanos al cliente y con alta coordinacion entre personas.',
  },
} as const

const ALERT_COPY: Record<string, string> = {
  E: 'Predomina un estilo estable y consistente.',
  EI: 'Se observa una combinacion entre estabilidad e impulso.',
  'EE:II': 'El equipo mezcla respuestas opuestas; conviene alinear prioridades.',
  IE: 'Hay señales de exigencia alta con necesidad de contencion.',
  I: 'El equipo muestra tension sostenida; conviene revisar cargas y acompanamiento.',
}

const optionsData = [
  {
    name: 'Resumen',
    value: '1',
  },
  {
    name: 'Dominancia',
    value: '2',
  },
  {
    name: 'Talentos',
    value: '3',
  },
  {
    name: 'Gráfico',
    value: '4',
  },
  {
    name: 'Plan',
    value: '5',
  },
]

const TAB_DESCRIPTIONS: Record<string, string> = {
  '2': 'Dominancia muestra en qué cuadrante tiene cada evaluado su predominio principal.',
  '3': 'Talentos muestra los evaluados que tienen ese cuadrante en un nivel alto de desarrollo, es decir, superior a 80 puntos.',
}

export interface TeamMapProps {
  betesaId?: string
  processId?: string
  usersByQuadrant?: QuadrantResults
  talents?: boolean
}

export interface QuadrantResultsResponse {
  id: number
  full_name: string
  name: string
  user_id: number
  domination: string
  alert_level: string
  quadrant_develop: string
  FI: string
  BI: string
  BD: string
  FD: string
}

export interface QuadrantResults {
  fi: QuadrantResultsResponse[]
  bi: QuadrantResultsResponse[]
  bd: QuadrantResultsResponse[]
  fd: QuadrantResultsResponse[]
}

export interface BrainResultsResponse {
  FI: number
  BI: number
  BD: number
  FD: number
}

type QuadrantCode = keyof typeof QUADRANT_DETAILS
type BetesaUser = BetesaResultsResponse['users'][number]

const getQuadrantOrder = (users: BetesaUser[]) => {
  const base = { FI: 0, FD: 0, BI: 0, BD: 0 }

  users.forEach((user) => {
    const dominance = user.domination as QuadrantCode
    if (dominance in base) {
      base[dominance] += 1
    }
  })

  return Object.entries(base).sort(([, a], [, b]) => b - a) as Array<[QuadrantCode, number]>
}

const getAlertOrder = (users: BetesaUser[]) => {
  const base = users.reduce<Record<string, number>>((acc, user) => {
    const key = user.alert_level || 'Sin dato'
    acc[key] = (acc[key] || 0) + 1
    return acc
  }, {})

  return Object.entries(base).sort(([, a], [, b]) => b - a)
}

const TeamMap: FC<TeamMapProps> = ({ betesaId, processId }) => {
  const { getError } = useNotification()

  const [selectedOption, setSelectedOption] = useState<string>('1')
  const [betesaResults, setBetesaResults] = useState<BetesaResultsResponse>({} as BetesaResultsResponse)
  const [page, setPage] = useState<number>(1)
  const [usersQuadrants, setUsersQuadrants] = useState<QuadrantResults>({} as QuadrantResults)
  const [talentQuadrants, setTalentQuadrants] = useState<QuadrantResults>({} as QuadrantResults)
  const [planQuadrants, setPlanQuadrants] = useState<QuadrantResults>({} as QuadrantResults)
  const [brainResponse, setBrainResponse] = useState<BrainResultsResponse>({} as BrainResultsResponse)
  const [brainIdealProfile, setBrainIdealProfile] = useState<BrainIdealProfile>({} as BrainIdealProfile)
  const [loading, setLoading] = useState<boolean>(Boolean(betesaId))
  const [hasLoadedBetesaResults, setHasLoadedBetesaResults] = useState<boolean>(false)
  const users = useMemo(() => betesaResults?.users || [], [betesaResults])

  const teamInsights = useMemo(() => {
    if (!users.length) {
      return null
    }

    const totalUsers = users.length
    const quadrantRanking = getQuadrantOrder(users)
    const alertRanking = getAlertOrder(users)
    const planRanking = Object.entries(
      users.reduce<Record<string, number>>((acc, user) => {
        const key = user.quadrant_develop || 'Sin plan'
        acc[key] = (acc[key] || 0) + 1
        return acc
      }, {}),
    ).sort(([, a], [, b]) => b - a)

    const [topQuadrant, topQuadrantCount] = quadrantRanking[0]
    const topQuadrantShare = Math.round((topQuadrantCount / totalUsers) * 100)
    const lowestQuadrantCount = quadrantRanking[quadrantRanking.length - 1][1]
    const spread = topQuadrantCount - lowestQuadrantCount
    const topAlert = alertRanking[0]
    const topAlertShare = topAlert ? Math.round((topAlert[1] / totalUsers) * 100) : 0
    const topPlan = planRanking[0]
    const topAlertDescription = topAlert ? ALERT_COPY[topAlert[0]] || `Predomina la alerta ${topAlert[0]}.` : ''

    let balanceTitle = 'Balanceado'
    let balanceHint = 'La distribucion permite combinar estilos sin una dominancia excesiva.'

    if (spread <= 1) {
      balanceTitle = 'Muy balanceado'
      balanceHint = 'La presencia entre cuadrantes es pareja y favorece la complementariedad.'
    } else if (topQuadrantShare >= 50) {
      balanceTitle = 'Concentrado'
      balanceHint = `Hay una carga fuerte hacia ${QUADRANT_DETAILS[topQuadrant].displayName.toLowerCase()}.`
    } else if (topQuadrantShare >= 40) {
      balanceTitle = 'Con sesgo moderado'
      balanceHint = `Se nota una preferencia por ${QUADRANT_DETAILS[topQuadrant].summary}.`
    }

    let primaryRisk = 'No se observan desequilibrios criticos en la lectura general.'

    if (topQuadrantShare >= 50) {
      primaryRisk = `Alta concentracion en ${QUADRANT_DETAILS[topQuadrant].shortName}; el equipo puede perder diversidad al decidir.`
    } else if (topAlert && topAlert[0] === 'I') {
      primaryRisk = 'El nivel de alerta dominante sugiere tension sostenida en el equipo.'
    } else if (topAlertShare >= 40 && topAlert) {
      primaryRisk = `Predomina la alerta ${topAlert[0]}; conviene revisar presion, ritmo y acompanamiento.`
    } else if (topPlan && topPlan[0] !== 'Sin plan' && Number(topPlan[1]) / totalUsers >= 0.45) {
      primaryRisk = `Muchas personas convergen en el plan ${topPlan[0]}; puede haber una brecha comun de desarrollo.`
    }

    const alerts = [
      `Predominio ${QUADRANT_DETAILS[topQuadrant].shortName}: ${topQuadrantShare}% del equipo`,
      topAlert ? `Alerta dominante ${topAlert[0]}: ${topAlertShare}%. ${topAlertDescription}` : null,
      topPlan && topPlan[0] !== 'Sin plan' ? `Plan mas recurrente: ${topPlan[0]}` : 'Aun no hay un plan dominante de desarrollo',
    ].filter(Boolean) as string[]

    const recommendations = [
      topQuadrantShare >= 45
        ? `Sumar perfiles complementarios a ${QUADRANT_DETAILS[topQuadrant].shortName} en decisiones clave y comites.`
        : 'Mantener equipos mixtos al asignar proyectos de alta prioridad.',
      topAlert && topAlert[0] === 'I'
        ? 'Priorizar seguimiento de carga, conversaciones de cuidado y claridad de expectativas.'
        : 'Usar esta lectura para distribuir mejor roles, ritmos y responsabilidades.',
      topPlan && topPlan[0] !== 'Sin plan'
        ? `Alinear acciones de desarrollo alrededor de ${topPlan[0]} sin perder personalizacion.`
        : 'Consolidar un frente comun de desarrollo cuando el proceso tenga mas avance.',
    ]

    return {
      totalUsers,
      predominanceTitle: `${QUADRANT_DETAILS[topQuadrant].shortName} · ${QUADRANT_DETAILS[topQuadrant].displayName}`,
      predominanceHint: `${topQuadrantShare}% del equipo se concentra en ${QUADRANT_DETAILS[topQuadrant].summary}.`,
      balanceTitle,
      balanceHint,
      primaryRisk,
      environmentText:
        topQuadrantShare <= 35
          ? 'El equipo responde mejor en entornos mixtos, con retos diversos y colaboracion entre estilos.'
          : QUADRANT_DETAILS[topQuadrant].environment,
      alerts,
      recommendations,
    }
  }, [users])

  useEffect(() => {
    if (betesaId === '') {
      setBetesaResults({} as BetesaResultsResponse)
      setHasLoadedBetesaResults(true)
      setLoading(false)
      return
    }

    setLoading(true)
    setHasLoadedBetesaResults(false)

    Promise.all([
      getBetesaResults(betesaId, page),
      getBrainIdealProfile(processId ? Number(processId) : 0),
    ]).then(
      ([betesaResponse, idealProfileResponse]) => {
        if (betesaResponse?.data) {
          setBetesaResults(betesaResponse.data)
        } else {
          setBetesaResults({} as BetesaResultsResponse)
        }

        if (idealProfileResponse?.data) {
          setBrainIdealProfile(idealProfileResponse.data)
        }
      },
      () => {
        getError('Ha ocurrido un error al cargar las pruebas betesa')
      },
    ).finally(() => {
      setHasLoadedBetesaResults(true)
      setLoading(false)
    })
  }, [betesaId, getError, page, processId])

  useEffect(() => {
    if (betesaId === '') {
      return
    }

    getBrainIdealProfile(processId ? Number(processId) : 0).then(
      (response) => {
        if (response?.data) {
          setBrainIdealProfile(response.data)
        }
      },
      () => {
        setLoading(false)
      }
    )
  }, [betesaId, processId])

  useEffect(() => {
    if (betesaId === '') {
      return
    }

    const fetchQuadrantResults = async () => {
      try {
        const fi = await getQuadrantResults(betesaId, 'FI')
        if (fi?.data) {
          setUsersQuadrants((prevState) => ({ ...prevState, fi: fi.data }))
        }
        const bi = await getQuadrantResults(betesaId, 'BI')
        if (bi?.data) {
          setUsersQuadrants((prevState) => ({ ...prevState, bi: bi.data }))
        }
        const bd = await getQuadrantResults(betesaId, 'BD')

        if (bd?.data) {
          setUsersQuadrants((prevState) => ({ ...prevState, bd: bd.data }))
        }
        const fd = await getQuadrantResults(betesaId, 'FD')
        if (fd?.data) {
          setUsersQuadrants((prevState) => ({ ...prevState, fd: fd.data }))
        }
      } catch (error) {
        getError('Ha ocurrido un error al cargar los resultados de los cuadrantes')
      }
    }

    fetchQuadrantResults()
  }, [betesaId, getError])

  useEffect(() => {
    if (betesaId === '') {
      return
    }

    const fetchTalentResults = async () => {
      try {
        const fi = await getTalentsResults(betesaId, 'FI')
        if (fi?.data) {
          setTalentQuadrants((prevState) => ({ ...prevState, fi: fi.data }))
        }
        const bi = await getTalentsResults(betesaId, 'BI')
        if (bi?.data) {
          setTalentQuadrants((prevState) => ({ ...prevState, bi: bi.data }))
        }
        const bd = await getTalentsResults(betesaId, 'BD')
        if (bd?.data) {
          setTalentQuadrants((prevState) => ({ ...prevState, bd: bd.data }))
        }
        const fd = await getTalentsResults(betesaId, 'FD')
        if (fd?.data) {
          setTalentQuadrants((prevState) => ({ ...prevState, fd: fd.data }))
        }
      } catch (error) {
        getError('Ha ocurrido un error al cargar los resultados de los cuadrantes')
      }
    }

    fetchTalentResults()
  }, [betesaId, getError])

  useEffect(() => {
    if (betesaId === '') {
      return
    }

    const fetchPlanResults = async () => {
      try {
        const fi = await getPlanResults(betesaId, 'FI')
        if (fi?.data) {
          setPlanQuadrants((prevState) => ({ ...prevState, fi: fi.data }))
        }
        const bi = await getPlanResults(betesaId, 'BI')
        if (bi?.data) {
          setPlanQuadrants((prevState) => ({ ...prevState, bi: bi.data }))
        }
        const bd = await getPlanResults(betesaId, 'BD')
        if (bd?.data) {
          setPlanQuadrants((prevState) => ({ ...prevState, bd: bd.data }))
        }
        const fd = await getPlanResults(betesaId, 'FD')
        if (fd?.data) {
          setPlanQuadrants((prevState) => ({ ...prevState, fd: fd.data }))
        }
      } catch (error) {
        getError('Ha ocurrido un error al cargar los resultados de los cuadrantes')
      }
    }

    fetchPlanResults()
  }, [betesaId, getError])

  useEffect(() => {
    if (betesaId === '') {
      return
    }
    getBrainResults(betesaId).then(
      (response) => {
        if (response?.data) {
          setBrainResponse(response.data)
        }
      },
      () => {
        getError('Ha ocurrido un error al cargar los promedios de los usuarios')
      }
    )
  }, [betesaId, getError])

  const handleSaveBrainIdealProfile = async (ideal: BrainIdealProfile) => {
    const response = await saveBrainIdealProfile(Number(processId) || 0, ideal)
    if (response?.status === 'OK') {
      setBrainIdealProfile(ideal)
    } else {
      getError('Ha ocurrido un error al guardar el perfil ideal de la gráfica')
    }
  }

  return (
    <Container>
      {loading ? (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          <Loader text="Cargando mapa Betesa..." />
        </div>
      ) : Object.keys(betesaResults).length > 0 ? (
        <SelectorContainer>
          <HorizontalSelector items={optionsData} selected={selectedOption} setSelected={setSelectedOption} appearance="segmented" />
          {(selectedOption === '2' || selectedOption === '3') && (
            <ContextNote>
              <ContextNoteText>{TAB_DESCRIPTIONS[selectedOption]}</ContextNoteText>
            </ContextNote>
          )}
          {selectedOption === '1' && teamInsights && (
            <InsightsStack>
              <SummaryGrid>
                <SummaryCard>
                  <SummaryLabel>Predominio del equipo</SummaryLabel>
                  <SummaryValue>{teamInsights.predominanceTitle}</SummaryValue>
                  <SummaryHint>{teamInsights.predominanceHint}</SummaryHint>
                </SummaryCard>
                <SummaryCard>
                  <SummaryLabel>Balance del equipo</SummaryLabel>
                  <SummaryValue>{teamInsights.balanceTitle}</SummaryValue>
                  <SummaryHint>{teamInsights.balanceHint}</SummaryHint>
                </SummaryCard>
                <SummaryCard>
                  <SummaryLabel>Riesgo principal</SummaryLabel>
                  <SummaryValue>Lectura ejecutiva</SummaryValue>
                  <SummaryHint>{teamInsights.primaryRisk}</SummaryHint>
                </SummaryCard>
              </SummaryGrid>

              <EnvironmentBanner>
                <EnvironmentTitle>Indicador de entorno recomendado</EnvironmentTitle>
                <EnvironmentText>{teamInsights.environmentText}</EnvironmentText>
              </EnvironmentBanner>

              <InsightSection>
                <InsightSectionTitle>Alertas del equipo</InsightSectionTitle>
                <InsightPills>
                  {teamInsights.alerts.map((alert, index) => (
                    <InsightPill key={`alert-${index}`}>{alert}</InsightPill>
                  ))}
                </InsightPills>
              </InsightSection>

              <InsightSection>
                <InsightSectionTitle>Recomendaciones accionables</InsightSectionTitle>
                <InsightPills>
                  {teamInsights.recommendations.map((recommendation, index) => (
                    <InsightPill key={`recommendation-${index}`}>{recommendation}</InsightPill>
                  ))}
                </InsightPills>
              </InsightSection>
            </InsightsStack>
          )}

          <ContentCard>
            {selectedOption === '1' && <TeamMapGrid results={betesaResults} handlePagination={setPage} />}
            {selectedOption === '2' && <BrainMap usersByQuadrant={usersQuadrants} variant="dominance" />}
            {selectedOption === '3' && <BrainMap usersByQuadrant={talentQuadrants} talents variant="talents" />}
            {selectedOption === '4' && (
              <div
                style={{
                  marginTop: '20px',
                  width: '100%',
                }}
              >
                <div>
                  {Object.keys(brainResponse).length > 0 ? (
                    <BrainGraphicContainer
                      FI={brainResponse.FI}
                      FD={brainResponse.FD}
                      BD={brainResponse.BD}
                      BI={brainResponse.BI}
                      idealProfile={brainIdealProfile}
                      saveBrainIdealProfile={handleSaveBrainIdealProfile}
                    />
                  ) : (
                    <NoDataContainer>
                      <NoDataSubContainer>
                        <NoDataIcon src={iconInfo} />
                        <TextContainer>
                          <NoDataText>Error al cargar los datos del perfil ideal</NoDataText>
                        </TextContainer>
                      </NoDataSubContainer>
                    </NoDataContainer>
                  )}
                </div>
              </div>
            )}
            {selectedOption === '5' && <BrainMap usersByQuadrant={planQuadrants} variant="plan" />}
          </ContentCard>
        </SelectorContainer>
      ) : !betesaId ? (
        <NoDataContainer>
          <NoDataSubContainer>
            <NoDataIcon src={iconInfo} />
            <TextContainer>
              <NoDataText>No hay datos de Betesa en este proceso</NoDataText>
            </TextContainer>
          </NoDataSubContainer>
        </NoDataContainer>
      ) : hasLoadedBetesaResults ? (
        <NoDataContainer>
          <NoDataSubContainer>
            <NoDataIcon src={iconInfo} />
            <TextContainer>
              <NoDataText>Los usuarios no han contestado la prueba</NoDataText>
            </TextContainer>
          </NoDataSubContainer>
        </NoDataContainer>
      ) : null}
    </Container>
  )
}

export default TeamMap
